import { Component, ContentChild, HostBinding, Input, Renderer2 } from '@angular/core';
import {
  inputAppendStyles,
  inputContainerStyles,
  inputIconsStyles,
  InputProps,
  inputStyles,
} from '@kite_aerolab/shared/components';
import { InputDirective } from './input.directive';

@Component({
  selector: 'kite-input-group',
  templateUrl: './input.component.html',
})
export class InputComponent {
  isPasswordVisible = false;
  isFocused = false;
  type = 'text';

  /**
   *
   * Obtener los estados del input
   *
   * */
  @ContentChild(InputDirective, { static: false }) formControl: InputDirective;
  /**
   *
   * Propiedades del contenedor
   *
   * */
  /**
   * Label del campo
   */
  @Input() label: string;
  /**
   * Mensaje de ayuda
   */
  @Input() helper: string;
  /**
   * Mensaje de error
   */
  @Input() error: string;
  /**
   * CSS props del input
   */
  @Input() css: InputProps['css'];
  /**
   * Tamaño del input. Puede ser medium o large.
   */
  @Input() size: InputProps['size'] = 'md';
  /**
   * Estado invalido
   */
  @Input() isInvalid: boolean;
  /**
   * Estado valido
   */
  @Input() isValid: boolean;
  /**
   * Agrega la opción de limpiar el campo
   */
  @Input() isClearable = true;
  /**
   * Alineación del texto y placeholder
   */
  @Input() textAlign: InputProps['textAlign'] = 'left';
  /**
   * Variante del input. Puede ser default o ghost.
   */
  @Input() variant: InputProps['variant'];

  // eslint-disable-next-line no-useless-constructor
  constructor(private renderer: Renderer2) {}

  @HostBinding('class.focus') get focus() {
    return this.formControl ? this.formControl.focus : false;
  }

  @HostBinding('class.invalid') get invalid() {
    return this.formControl ? this.formControl.isInvalid() : false;
  }

  @HostBinding('class.disabled') get disabled() {
    return this.formControl ? this.formControl.isDisabled() : false;
  }

  @HostBinding('class.disabled') get id() {
    return this.formControl ? this.formControl.getID() : '';
  }

  @HostBinding() get value() {
    return this.formControl ? this.formControl.value : '';
  }

  /**
   * Estilos del input
   */
  public get inputStyles(): string[] {
    return [
      inputStyles({
        isInvalid: this.isInvalid,
        isValid: this.isValid,
        isDisabled: this.disabled,
        size: this.size,
        textAlign: this.textAlign,
        variant: this.variant,
      }).className,
    ];
  }

  /**
   * Estilos del contenedor
   */
  public get inputContainerStyles(): string[] {
    return [inputContainerStyles({ css: this.css }).className];
  }

  /**
   * Estilos del append
   */
  public get inputAppendStyles(): string[] {
    return [inputAppendStyles().className];
  }

  /**
   * Estilos de los íconos
   */
  public get inputIconsStyles(): string[] {
    return [inputIconsStyles().className];
  }

  ngAfterViewInit() {
    if (this.formControl) {
      this.type = this.formControl.getType();
      if (this.isInvalid) {
        this.renderer.setAttribute(this.formControl.el.nativeElement, 'aria-invalid', String(this.isInvalid));
      }
      if (this.isValid) {
        this.renderer.setAttribute(this.formControl.el.nativeElement, 'aria-valid', String(this.isValid));
      }
      if (this.error || this.label) {
        this.renderer.setAttribute(
          this.formControl.el.nativeElement,
          'aria-describedby',
          String(!this.isInvalid ? this.id + '-helptext' : this.id + '-feedback')
        );
      }
    } else {
      throw 'the kite-input-group component should include an input element with the kiteInput directive (for example: <input kiteInput type="text"/>). For more information you can view the documentation at https://paper.dropbox.com/doc/In-progress-Kite-UI-Documentation-for-users-6QOP7bqjW9MHEFtDcKgT7';
    }
  }

  /**
   * Clear del input
   */
  clearText() {
    this.formControl.clear();
  }

  /**
   * Muestra el password
   */
  showPassword() {
    this.formControl.setType('text');
    this.isPasswordVisible = true;
  }

  /**
   * Oculta el password
   */
  hidePassword() {
    this.formControl.setType('password');
    this.isPasswordVisible = false;
  }

  focusAppend() {
    this.isFocused = true;
  }

  focusOutAppend() {
    this.isFocused = false;
  }
}
